import { Upload } from "@mui/icons-material";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { CheckBoxField } from "@pimo/pimo-components/src/lib/molecules";
import {
  Attachment,
  AUTOMATION_COMPLEXITY_TYPES,
  AUTOMATION_METHODOLOGY_TYPE,
  AUTOMATION_SOLUTION_IMPLEMENTATION_PROVIDER_TYPE,
  AUTOMATION_UNDERLYING_PLATFORM_TYPES,
  BUSINESS_UNIT_TYPE,
  CATEGORY_TYPES,
  COMMERCIAL_LINES_OF_BUSINESS,
  EXTENDED_LINES_OF_BUSINESS,
  FUNCTION_TYPES,
  IMPLEMENTATION_PROGRAM_TYPES,
  Initiative,
  INITIATIVE_ENABLER_TYPES,
  INITIATIVE_PHASES,
  INTER_OE_REGION_TYPE,
  isAttachment,
  LINES_OF_BUSINESS,
  PROCESS_MINING_ENABLEMENT_TYPE,
  UnsavedAttachment,
} from "aint-types";
import { useWatch } from "react-hook-form";

import { FormFieldWrapper } from "../../../components/form-fields/form-field-wrapper";
import { INFormFieldInfoSlot } from "../../../components/form-fields/in-form-field-info-slot";
import { OptionalLabelText } from "../../../components/optional-label-text";
import { SupportingDocument } from "../../../components/supporting-document";
import { FORM_FIELD_INFO_TEXT } from "../../../form/form-field-info-text";
import { RichTextFormField } from "../../../form/form-fields/rich-text-field";
import { SelectField } from "../../../form/form-fields/select-field";
import { TextFormField } from "../../../form/form-fields/text-field";
import { EditInitiativeDialogFireEventType } from "./edit-initiative-dialog";

const Headline = styled("h1")(() => ({
  fontWeight: 700,
  fontSize: "16px",
}));

const CheckboxWrapper = styled(Grid)(() => ({
  ".MuiFormGroup-root": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "220px",
  },
}));

type EditInitiativeBaseTabProps = {
  isCreate: boolean;
  showInterOeRegionField: boolean;
  showBusinessUnitField: boolean;
  showCategoryField: boolean;
  showOeProgramFields: boolean;
  showElevateImplProgramOption: boolean;
  showExtendedLobOptions: boolean;
  usersWithAccessToOE: string[];
  fireEvent?: EditInitiativeDialogFireEventType;
  supportingDocuments?: (UnsavedAttachment | Attachment)[];
};

export function EditInitiativeBaseTab({
  showInterOeRegionField,
  showBusinessUnitField,
  showCategoryField,
  showOeProgramFields,
  showElevateImplProgramOption,
  showExtendedLobOptions,
  usersWithAccessToOE,
  isCreate,
  fireEvent,
  supportingDocuments,
}: EditInitiativeBaseTabProps) {
  const theme = useTheme();
  return (
    <Grid
      container
      flexDirection="column"
      rowSpacing="12px"
      sx={{ marginTop: theme.spacing(3) }}
      size={12}
    >
      <Headline sx={{ fontSize: "22px" }}>Initiative</Headline>
      <ProfileSection isCreate={isCreate} />
      <ScopeSection
        showExtendedLobOptions={showExtendedLobOptions}
        showInterOeRegionField={showInterOeRegionField}
        showBusinessUnitField={showBusinessUnitField}
        showCategoryField={showCategoryField}
        showOeProgramFields={showOeProgramFields}
      />
      <OwnershipSection
        usersWithAccessToOE={usersWithAccessToOE}
        showElevateImplProgramOption={showElevateImplProgramOption}
      />
      <EnablersSection />
      <AdditionalInfoSection
        supportingDocuments={supportingDocuments}
        fireEvent={fireEvent}
      />
    </Grid>
  );
}

export function ProfileSection({ isCreate }: { isCreate: boolean }) {
  return (
    <Grid container flexDirection="column" rowSpacing="20px" size={12}>
      <Grid>
        <Headline>Profile</Headline>
      </Grid>
      {!isCreate && (
        <Grid>
          <FormFieldWrapper
            label="Initiative ID"
            uiOptions={{ narrowInfoSlotIcon: true }}
          >
            <TextFormField disabled name="uid" />
          </FormFieldWrapper>
        </Grid>
      )}

      <Grid>
        <FormFieldWrapper
          label="Initiative Name*"
          uiOptions={{ narrowInfoSlotIcon: true }}
        >
          <TextFormField name="initiativeName" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Initiative Description*"
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["initiativeDescription"]}
            />
          }
        >
          <RichTextFormField name="initiativeDescription" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Phase*"
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["initiativePhase"]}
            />
          }
        >
          <SelectField
            name="initiativePhase"
            options={INITIATIVE_PHASES.map((type) => type)}
          />
        </FormFieldWrapper>
      </Grid>
    </Grid>
  );
}

export function ScopeSection({
  showInterOeRegionField,
  showBusinessUnitField,
  showCategoryField,
  showOeProgramFields,
  showExtendedLobOptions,
}: {
  showInterOeRegionField: boolean;
  showBusinessUnitField: boolean;
  showCategoryField: boolean;
  showOeProgramFields: boolean;
  showExtendedLobOptions: boolean;
}) {
  const [
    primaryLineOfBusiness,
    secondaryLineOfBusiness,
    itChangeRequired,
    oeProgram,
    primaryFunction,
  ] = useWatch({
    name: [
      "primaryLineOfBusiness",
      "secondaryLineOfBusiness",
      "itChangeRequired",
      "oeProgram",
      "primaryFunction",
    ],
  }) as [
    Initiative["primaryLineOfBusiness"],
    Initiative["secondaryLineOfBusiness"],
    Initiative["itChangeRequired"],
    Initiative["oeProgram"],
    Initiative["primaryFunction"],
  ];

  const baseFunctionOptions = Array.from(FUNCTION_TYPES) as string[];
  const functionOptions = showExtendedLobOptions
    ? baseFunctionOptions
    : baseFunctionOptions.filter((option) => option !== "Other");

  const lineOfBusinessOptions = showExtendedLobOptions
    ? // Move other to the end of the list
      [
        ...LINES_OF_BUSINESS.slice(0, -1),
        ...EXTENDED_LINES_OF_BUSINESS,
        "Other",
      ]
    : LINES_OF_BUSINESS;

  return (
    <Grid container flexDirection="column" rowSpacing="20px" size={12}>
      <Grid>
        <Headline>Scope</Headline>
      </Grid>
      <Grid container size={12}>
        <CheckboxWrapper>
          <FormFieldWrapper
            uiOptions={{ centerItems: true, narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["itChangeRequired"]}
              />
            }
            fieldWrapperSx={{
              minWidth: "180px",
            }}
          >
            <CheckBoxField name="itChangeRequired" label="IT Change required" />
          </FormFieldWrapper>
        </CheckboxWrapper>
        {showOeProgramFields && (
          <CheckboxWrapper>
            <FormFieldWrapper
              uiOptions={{ centerItems: true, narrowInfoSlotIcon: true }}
              fieldWrapperSx={{
                minWidth: "180px",
              }}
            >
              <CheckBoxField name="oeProgram" label="OE Program" />
            </FormFieldWrapper>
          </CheckboxWrapper>
        )}
        {itChangeRequired === true && (
          <Grid size={oeProgram ? 6 : 12}>
            <FormFieldWrapper
              label="IT Project ID*"
              uiOptions={{ narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot
                  text={FORM_FIELD_INFO_TEXT["itProjectId"]}
                />
              }
            >
              <TextFormField name="itProjectId" />
            </FormFieldWrapper>
          </Grid>
        )}
        {oeProgram === true && (
          <Grid size={itChangeRequired ? 6 : 12}>
            <FormFieldWrapper
              label="OE Program #*"
              uiOptions={{ narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot
                  text={FORM_FIELD_INFO_TEXT["oeProgramNr"]}
                />
              }
            >
              <TextFormField name="oeProgramNr" />
            </FormFieldWrapper>
          </Grid>
        )}
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Affected Team/Department{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["affectedTeam"]} />
          }
        >
          <TextFormField name="affectedTeam" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Primary Function Affected*"
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["primaryFunction"]}
            />
          }
        >
          <SelectField
            name="primaryFunction"
            options={functionOptions.map((option) => option)}
          />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Primary Subfunction Affected{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["primarySubFunction"]}
            />
          }
        >
          <TextFormField name="primarySubFunction" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Secondary Functions Affected{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["secondaryFunctions"]}
            />
          }
        >
          <SelectField
            name="secondaryFunctions"
            multiple
            options={functionOptions
              .filter((option) => option !== primaryFunction)
              .map((option) => option)}
          />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Primary LoB Affected*"
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["primaryLineOfBusiness"]}
            />
          }
        >
          <SelectField
            name="primaryLineOfBusiness"
            options={lineOfBusinessOptions.map((type) => type)}
          />
        </FormFieldWrapper>
      </Grid>
      {primaryLineOfBusiness === "Other" && (
        <Grid>
          <FormFieldWrapper
            label="Primary LoB Other*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["primaryLineOfBusinessOther"]}
              />
            }
          >
            <TextFormField name="primaryLineOfBusinessOther" />
          </FormFieldWrapper>
        </Grid>
      )}
      {primaryLineOfBusiness === "Commercial" && (
        <Grid>
          <FormFieldWrapper
            label="Commercial Line of Business*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["commercialLineOfBusiness"]}
              />
            }
          >
            <SelectField
              name="commercialLineOfBusiness"
              options={COMMERCIAL_LINES_OF_BUSINESS.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Secondary LoB(s) Affected{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["secondaryLineOfBusiness"]}
            />
          }
        >
          <SelectField
            name="secondaryLineOfBusiness"
            multiple
            options={lineOfBusinessOptions
              .filter((type) => type !== primaryLineOfBusiness)
              .map((type) => type)}
          />
        </FormFieldWrapper>
      </Grid>
      {secondaryLineOfBusiness?.includes("Other") && (
        <Grid>
          <FormFieldWrapper
            label="Secondary LoB Other*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["secondaryLineOfBusinessOther"]}
              />
            }
          >
            <TextFormField name="secondaryLineOfBusinessOther" />
          </FormFieldWrapper>
        </Grid>
      )}
      {showInterOeRegionField && (
        <Grid>
          <FormFieldWrapper
            label={
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, fontSize: "14px" }}
                gutterBottom
              >
                Inter-OE Region{" "}
                <OptionalLabelText>(Optional)</OptionalLabelText>
              </Typography>
            }
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["interOeRegion"]}
              />
            }
          >
            <SelectField
              name="interOeRegion"
              options={INTER_OE_REGION_TYPE.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      {showBusinessUnitField && (
        <Grid>
          <FormFieldWrapper
            label={
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, fontSize: "14px" }}
                gutterBottom
              >
                Business Unit <OptionalLabelText>(Optional)</OptionalLabelText>
              </Typography>
            }
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["businessUnit"]}
              />
            }
          >
            <SelectField
              name="businessUnit"
              multiple
              options={BUSINESS_UNIT_TYPE.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      {showCategoryField && (
        <>
          <Grid>
            <FormFieldWrapper
              label={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, fontSize: "14px" }}
                  gutterBottom
                >
                  Category*
                </Typography>
              }
              uiOptions={{ narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["category"]} />
              }
            >
              <SelectField
                name="category"
                options={CATEGORY_TYPES.map((type) => type)}
              />
            </FormFieldWrapper>
          </Grid>
          {showExtendedLobOptions && (
            <Grid>
              <FormFieldWrapper
                label={
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontSize: "14px" }}
                    gutterBottom
                  >
                    Sub Category{" "}
                    <OptionalLabelText>(Optional)</OptionalLabelText>
                  </Typography>
                }
                uiOptions={{ narrowInfoSlotIcon: true }}
              >
                <TextFormField name="subCategory" />
              </FormFieldWrapper>
            </Grid>
          )}
        </>
      )}
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Initiative Sponsor(s){" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["initiativeSponsors"]}
            />
          }
        >
          <TextFormField name="initiativeSponsors" />
        </FormFieldWrapper>
      </Grid>
    </Grid>
  );
}

export function OwnershipSection({
  usersWithAccessToOE,
  showElevateImplProgramOption,
}: {
  usersWithAccessToOE: string[];
  showElevateImplProgramOption: boolean;
}) {
  const implementationProgram = useWatch({
    name: "implementationProgram",
  }) as Initiative["implementationProgram"];

  const implementationProgramOptions: (
    | (typeof IMPLEMENTATION_PROGRAM_TYPES)[number]
    | "Elevate"
  )[] = IMPLEMENTATION_PROGRAM_TYPES.map((type) => type);

  if (showElevateImplProgramOption) {
    implementationProgramOptions.splice(-1, 0, "Elevate");
  }
  return (
    <Grid container flexDirection="column" rowSpacing="20px" size={12}>
      <Grid>
        <Headline>Ownership</Headline>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Initiative Owner*
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["initiativeOwner"]}
            />
          }
        >
          <SelectField
            name="initiativeOwner"
            multiple
            options={usersWithAccessToOE.map((email) => ({
              label: email,
              value: email,
            }))}
          />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Implementation Program*"
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["implementationProgram"]}
            />
          }
        >
          <SelectField
            name="implementationProgram"
            options={implementationProgramOptions}
          />
        </FormFieldWrapper>
      </Grid>
      {implementationProgram === "Other" && (
        <Grid>
          <FormFieldWrapper
            label="Implementation Program Other*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["implementationProgramOther"]}
              />
            }
          >
            <TextFormField name="implementationProgramOther" />
          </FormFieldWrapper>
        </Grid>
      )}
    </Grid>
  );
}

export function AdditionalInfoSection({
  fireEvent,
  supportingDocuments,
}: {
  fireEvent?: EditInitiativeDialogFireEventType;
  supportingDocuments?: (UnsavedAttachment | Attachment)[];
}) {
  return (
    <Grid container flexDirection="column" rowSpacing="20px" size={12}>
      <Grid>
        <Headline>Additional Information</Headline>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              KPI Dashboard Link{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["kpiDashboardHyperlink"]}
            />
          }
        >
          <TextFormField name="relatedKPIDashboardHyperlink" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Local Initiative Cluster{" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["localInitiativeCluster"]}
            />
          }
        >
          <TextFormField name="localInitiativeCluster" />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper label="Supporting Documents">
          <Button
            startIcon={<Upload />}
            sx={{
              marginTop: "8px",
              textTransform: "unset",
            }}
            onClick={() =>
              fireEvent?.("edit-dialog:upload-document-button-click")
            }
          >
            Upload document
          </Button>
        </FormFieldWrapper>
        {(supportingDocuments ?? []).map((doc, index) => {
          return (
            <SupportingDocument
              key={`supportingDocument-${isAttachment(doc) ? doc.id : `${doc.file.name}-${index}`}`}
              fireEvent={fireEvent}
              doc={doc}
              documentIndex={index}
            />
          );
        })}
      </Grid>
    </Grid>
  );
}

export function EnablersSection() {
  const [initiativeEnablers, processMiningEnablementType] = useWatch({
    name: ["initiativeEnablers", "processMiningEnablementType"],
  }) as [
    Initiative["initiativeEnablers"],
    Initiative["processMiningEnablementType"],
  ];

  return (
    <Grid container flexDirection="column" rowSpacing="20px" size={12}>
      <Grid>
        <Headline>Enablers</Headline>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              Enabler <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          uiOptions={{ narrowInfoSlotIcon: true }}
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["initiativeEnabler"]}
            />
          }
        >
          <SelectField
            name="initiativeEnablers"
            multiple
            options={INITIATIVE_ENABLER_TYPES.map((type) => type)}
          />
        </FormFieldWrapper>
      </Grid>
      {initiativeEnablers?.includes("Process Mining") && (
        <Grid>
          <FormFieldWrapper
            label="Process Mining Enablement Type*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["processMiningEnablementType"]}
              />
            }
          >
            <SelectField
              name="processMiningEnablementType"
              multiple
              options={PROCESS_MINING_ENABLEMENT_TYPE.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}

      {initiativeEnablers?.includes("Process Mining") &&
        processMiningEnablementType?.includes("Other") && (
          <Grid>
            <FormFieldWrapper
              label="Process Mining Enablement Type Other*"
              uiOptions={{ narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot
                  text={
                    FORM_FIELD_INFO_TEXT["processMiningEnablementTypeOther"]
                  }
                />
              }
            >
              <TextFormField name="processMiningEnablementTypeOther" />
            </FormFieldWrapper>
          </Grid>
        )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid container size={12}>
          <CheckboxWrapper>
            <FormFieldWrapper
              uiOptions={{ centerItems: true, narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot
                  text={FORM_FIELD_INFO_TEXT["automationAIPowered"]}
                />
              }
              fieldWrapperSx={{
                minWidth: "230px",
              }}
            >
              <CheckBoxField
                name="automationAIPowered"
                label="Automation AI Powered*"
              />
            </FormFieldWrapper>
          </CheckboxWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid container size={12}>
          <CheckboxWrapper>
            <FormFieldWrapper
              uiOptions={{ centerItems: true, narrowInfoSlotIcon: true }}
              infoSlot={
                <INFormFieldInfoSlot
                  text={FORM_FIELD_INFO_TEXT["automationGenAIPowered"]}
                />
              }
              fieldWrapperSx={{
                minWidth: "230px",
              }}
            >
              <CheckBoxField
                name="automationGenAIPowered"
                label="Automation Gen AI Powered*"
              />
            </FormFieldWrapper>
          </CheckboxWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Complexity*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["automationComplexity"]}
              />
            }
          >
            <SelectField
              name="automationComplexity"
              options={AUTOMATION_COMPLEXITY_TYPES.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Involved System*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["automationInvolvedSystem"]}
              />
            }
          >
            <TextFormField name="automationInvolvedSystem" />
          </FormFieldWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Methodology*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["automationMethodology"]}
              />
            }
          >
            <SelectField
              name="automationMethodology"
              options={AUTOMATION_METHODOLOGY_TYPE.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Solution Implementation Provider*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={
                  FORM_FIELD_INFO_TEXT[
                    "automationSolutionImplementationProvider"
                  ]
                }
              />
            }
          >
            <SelectField
              name="automationSolutionImplementationProvider"
              options={AUTOMATION_SOLUTION_IMPLEMENTATION_PROVIDER_TYPE.map(
                (type) => type
              )}
            />
          </FormFieldWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Technical Solution*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["automationTechnicalSolution"]}
              />
            }
          >
            <TextFormField name="automationTechnicalSolution" />
          </FormFieldWrapper>
        </Grid>
      )}
      {initiativeEnablers?.includes("Automation") && (
        <Grid>
          <FormFieldWrapper
            label="Automation Underlying Platform*"
            uiOptions={{ narrowInfoSlotIcon: true }}
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["automationUnderlyingPlatform"]}
              />
            }
          >
            <SelectField
              name="automationUnderlyingPlatform"
              options={AUTOMATION_UNDERLYING_PLATFORM_TYPES.map((type) => type)}
            />
          </FormFieldWrapper>
        </Grid>
      )}
    </Grid>
  );
}
